import React, { useEffect, useMemo } from 'react';
import {
	Typography,
	Box,
	Card,
	useTheme,
	Link,
	TableContainer,
	Table,
	TableBody,
	TableRow,
	TableCell,
	useMediaQuery,
	Skeleton,
} from '@mui/material';
import { formatDate, policyHeadCells } from '@/utils/constants';
import { useGetDealPoliciesQuery, useGetDealRowQuery } from '@/features/deals/dealsApi';
import LinkIcon from '@mui/icons-material/Link';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { FolioTableHead } from '@/components/table/FolioTableHead';
import { getComparator, stableSort } from '@/components/table/functions';
import { PolicyRow } from '@/components/tables/PolicyRow';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { safelyParseDate } from '@/utils/date';

export const PolicyLinkedCard = ({ activity }) => {
	const dealId = activity.data.dealId;

	const location = useLocation();
	const baseTheme = useTheme();

	const policiesQuery = useGetDealPoliciesQuery({ dealId }, { skip: !dealId });

	const { data: dealRow } = useGetDealRowQuery(dealId, { skip: !dealId });

	const ownerDocId = dealRow?.ownerDocId;
	const { data: owner } = useGetUserDetailsQuery(
		{ userId: ownerDocId },
		{
			skip: !ownerDocId,
		},
	);

	const basePath = '/clients';
	const isClientView = location.pathname.includes(basePath);

	const order = 'desc';
	const orderBy = 'createdWhen';
	const headerCells = policyHeadCells.filter((header) => header.id !== 'checkbox');

	// const policyHeadCells = [
	// 	{
	// 		id: 'policyNumber',
	// 		numeric: false,
	// 		label: 'Policy number',
	// 		align: 'left',
	// 		sortable: false,
	// 	},
	// 	{
	// 		id: 'renewalDate',
	// 		numeric: false,
	// 		label: 'Renewal date',
	// 		align: 'left',
	// 		sortable: false,
	// 	},
	// 	{ id: 'description', numeric: false, label: 'Description', align: 'left', sortable: false },
	// 	{ id: 'insurer', numeric: false, label: 'Insurer', align: 'left', sortable: false },
	// ];

	const policyTransactions = useMemo(
		() => policiesQuery.data?.transactions ?? [],
		[policiesQuery.data],
	);

	useEffect(() => {
		console.log('PolicyLinkedCard policiesQuery.data', policiesQuery.data);
	}, [policiesQuery.data]);

	const matchingTransaction = useMemo(
		() => policyTransactions.find((t) => t.Id === activity.data?.policyTransactionId),
		[policyTransactions, activity.data?.policyTransactionId],
	);

	const fullName = owner?.name ?? 'Unknown';

	const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up('md'));
	// const displayCells = useMemo(() => {
	// 	const middleCells = policyHeadCells.slice(1, policyHeadCells.length - 1);
	// 	return isLargeScreen ? middleCells : middleCells.slice(0, 3);
	// }, [isLargeScreen, policyHeadCells]);

	const createdDate = useMemo(
		() => safelyParseDate(activity.data.createdDate),
		[activity.data.createdDate],
	);
	return (
		<Card
			key={`activity-linked-card-${dealId}`}
			sx={{
				marginBottom: '12px',
				borderRadius: '4px',
				padding: 2,
				boxShadow: baseTheme.shadows[1],
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
					<LinkIcon sx={{ color: baseTheme.palette.primary.main }} />
					<Typography variant='task_body' style={{ padding: '10px' }}>
						<span style={{ fontWeight: 'bold' }}>Policies linked</span> by{' '}
						<span>{fullName}</span>
						<span style={{ margin: '0 0.5em' }}>{' | '}</span>
						<span style={{ marginRight: 5, fontWeight: '500' }}>
							{formatDate(createdDate, true)}{' '}
						</span>
					</Typography>
				</Box>
				{!isClientView && (
					<Link
						to={`/clients/${activity.data.clientId}`}
						component={RouterLink}
						sx={{
							textDecorationLine: 'none',
							alignSelf: 'center', // Align link to the center vertically
							marginLeft: '10px',
						}}
					>
						View Client
					</Link>
				)}
			</Box>

			<TableContainer>
				<Table
					aria-labelledby='tableTitle'
					size='small'
					aria-label='enhanced table'
					sx={{ border: 'none', '& td, & th': { border: 'none' } }}
				>
					<FolioTableHead order={order} orderBy={orderBy} headCells={headerCells} />
					<TableBody>
						{matchingTransaction ? (
							<PolicyRow
								hideCheckbox={true}
								key={`policy-row-matching-${matchingTransaction.Id}`}
								policy={matchingTransaction}
							/>
						) : (
							<TableRow>
								<TableCell colSpan={policyHeadCells.length} align='center'>
									{policiesQuery.isLoading ? (
										<Skeleton width={'100%'} />
									) : (
										<Typography variant='subtitle1'>
											No policies linked
										</Typography>
									)}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Card>
	);
};
